import { data } from 'react-router'
import {
	CANCELED_BY_PATIENT_STATE,
	CANCELED_NO_RESPONSE_STATE,
	DONE_STATE,
} from '#app/utils/appointment-states-options.ts'
import { requireAuthenticated } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getUserBranchId } from '#app/utils/organization.server.ts'
import { requireUserWithPermission } from '#app/utils/permissions.server.ts'
import { UpdateAppointmentStatusSchema } from '#app/utils/schemas/update-appointment-status-schema.tsx'
import { type Route } from './+types/update-appointment-status.tsx'

export async function action({ request }: Route.ActionArgs) {
	const { userId, currentOrganizationId } = await requireAuthenticated(request)
	const branchId = await getUserBranchId(currentOrganizationId)

	const formData = await request.formData()
	const rawData = formData.get('data')

	if (typeof rawData !== 'string') {
		return data({ error: 'missing data' }, { status: 400 })
	}

	const parsedData = UpdateAppointmentStatusSchema.safeParse(
		JSON.parse(rawData),
	)

	if (!parsedData.success) {
		return data({ error: 'invalid data' }, { status: 400 })
	}

	const { appointmentId, newStateId, reason } = parsedData.data

	// Only users who can delete appointments can set "canceled_*" states
	if (
		newStateId === CANCELED_BY_PATIENT_STATE ||
		newStateId === CANCELED_NO_RESPONSE_STATE
	) {
		await requireUserWithPermission(request, 'delete:appointment:any')
	}

	// Get the appointment to log the old state and patient info
	const appointment = await prisma.appointment.findUnique({
		where: {
			id: appointmentId,
			organizationId: currentOrganizationId,
			branchId: branchId,
		},
		select: {
			stateId: true,
			patient: {
				select: {
					firstName: true,
					lastName: true,
				},
			},
		},
	})

	if (!appointment) {
		return data({ error: 'appointment not found' }, { status: 404 })
	}

	// Update the appointment status
	await prisma.appointment.update({
		where: {
			id: appointmentId,
			organizationId: currentOrganizationId,
			branchId: branchId,
		},
		data: {
			stateId: newStateId,
			updateUserId: userId,
		},
	})

	// Create an appointment log entry
	await prisma.appointmentLog.create({
		data: {
			appointmentId,
			stateId: newStateId,
			userId,
			reason: reason || null,
			date: new Date(),
		},
	})

	return {
		success: true,
		isMarkedAsDone: newStateId === DONE_STATE,
		appointmentId,
		patientName: appointment.patient
			? `${appointment.patient.firstName} ${appointment.patient.lastName}`
			: 'Unknown',
	}
}

export default () => null
